import { useQuery, useMutation } from '@apollo/client';
import { gql } from '@apollo/client';

// Types
export interface LegacyDocumentUpdateItem {
  documentId: string;
  document: {
    id: string;
    name: string;
    reference: string;
    jurisdiction: string;
    type: string;
    version_major: number;
    version_minor: number;
  };
  changeSummary?: string;
  updateType?: 'MAJOR' | 'MINOR';
  itemType?: string;
}

export interface DocumentUpdateNotification {
  id: string;
  createdAt: string;
  userId: string;
  documentId: string;
  document: {
    id: string;
    name: string;
    reference: string;
    jurisdiction: string;
    type: string;
    version_major: number;
    version_minor: number;
  };
  updateType: 'MAJOR' | 'MINOR';
  previousVersion: string;
  newVersion: string;
  isRead: boolean;
  readAt?: string;
  changesSummary?: {
    title?: string;
    description?: string;
    isSystemNotification?: boolean;
    [key: string]: any;
  };
  items?: LegacyDocumentUpdateItem[];
}

export interface NotificationStats {
  totalCount: number;
  unreadCount: number;
  lastNotificationDate?: string;
}

export interface NotificationConnection {
  nodes: DocumentUpdateNotification[];
  totalCount: number;
  unreadCount: number;
  hasNextPage: boolean;
  cursor?: string;
}

// GraphQL Queries
export const GET_NOTIFICATION_STATS = gql`
  query GetNotificationStats {
    notificationStats {
      totalCount
      unreadCount
      lastNotificationDate
    }
  }
`;

export const GET_USER_NOTIFICATIONS = gql`
  query GetUserNotifications($limit: Int, $cursor: String, $unreadOnly: Boolean) {
    userNotifications(limit: $limit, cursor: $cursor, unreadOnly: $unreadOnly) {
      nodes {
        id
        createdAt
        userId
        documentId
        document {
          id
          name
          reference
          jurisdiction
          type
          version_major
          version_minor
        }
        updateType
        previousVersion
        newVersion
        isRead
        readAt
        changesSummary
        items {
          documentId
          document {
            id
            name
            reference
            jurisdiction
            type
            version_major
            version_minor
          }
          changeSummary
          updateType
          itemType
        }
      }
      totalCount
      unreadCount
      hasNextPage
      cursor
    }
  }
`;

// GraphQL Mutations
export const MARK_NOTIFICATION_READ = gql`
  mutation MarkNotificationRead($input: MarkNotificationReadInput!) {
    markNotificationRead(input: $input) {
      id
      isRead
      readAt
    }
  }
`;

export const MARK_ALL_NOTIFICATIONS_READ = gql`
  mutation MarkAllNotificationsRead($input: MarkAllNotificationsReadInput) {
    markAllNotificationsRead(input: $input)
  }
`;

export const ACKNOWLEDGE_NOTIFICATION = gql`
  mutation AcknowledgeNotification($input: AcknowledgeNotificationInput!) {
    acknowledgeNotification(input: $input) {
      id
      isAcknowledged
      acknowledgedAt
    }
  }
`;

export const DISMISS_NOTIFICATION = gql`
  mutation DismissNotification($input: DismissNotificationInput!) {
    dismissNotification(input: $input) {
      id
      isDismissed
      dismissedAt
    }
  }
`;

// Hooks
export const useNotificationStats = () => {
  return useQuery<{ notificationStats: NotificationStats }>(GET_NOTIFICATION_STATS, {
    fetchPolicy: 'cache-and-network',
    pollInterval: 30000, // Poll every 30 seconds for real-time updates
    errorPolicy: 'all'
  });
};

export const useUserNotifications = (options?: {
  limit?: number;
  cursor?: string;
  unreadOnly?: boolean;
}) => {
  const { limit = 20, cursor, unreadOnly = false } = options || {};
  
  return useQuery<{ userNotifications: NotificationConnection }>(GET_USER_NOTIFICATIONS, {
    variables: { limit, cursor, unreadOnly },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all'
  });
};

export const useMarkNotificationRead = () => {
  return useMutation(MARK_NOTIFICATION_READ, {
    refetchQueries: [
      { query: GET_NOTIFICATION_STATS },
      { query: GET_USER_NOTIFICATIONS, variables: { limit: 20 } }
    ],
    errorPolicy: 'all'
  });
};

export const useMarkAllNotificationsRead = () => {
  return useMutation(MARK_ALL_NOTIFICATIONS_READ, {
    refetchQueries: [
      { query: GET_NOTIFICATION_STATS },
      { query: GET_USER_NOTIFICATIONS, variables: { limit: 20 } }
    ],
    errorPolicy: 'all'
  });
};

export const useAcknowledgeNotification = () => {
  return useMutation(ACKNOWLEDGE_NOTIFICATION, {
    refetchQueries: [
      { query: GET_NOTIFICATION_STATS },
      { query: GET_USER_NOTIFICATIONS, variables: { limit: 20 } },
      { query: GET_USER_NOTIFICATIONS, variables: { limit: 1, unreadOnly: true } }
    ],
    errorPolicy: 'all'
  });
};

export const useDismissNotification = () => {
  return useMutation(DISMISS_NOTIFICATION, {
    refetchQueries: [
      { query: GET_NOTIFICATION_STATS },
      { query: GET_USER_NOTIFICATIONS, variables: { limit: 20 } },
      { query: GET_USER_NOTIFICATIONS, variables: { limit: 1, unreadOnly: true } }
    ],
    errorPolicy: 'all'
  });
};
